import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { entryById, pageEntry, PAGE_GUIDE } from '../lib/guideSearch'

// The guide entry of the Hey Captain Guide v4 inside the app (guide-help): the "?" button in the page header opens the entry
// of the open page (src/lib/guideSearch.js PAGE_GUIDE), and "Open guide page" under an assistant answer opens the entry it
// answered from. The guide text is src/lib/guideHelp.json (scripts/guide-help-export.mjs), loaded only when first opened.
// Events: window 'hc-guide-open' { id } or { page }.

let DATA = null
const loadGuide = () => DATA ? Promise.resolve(DATA) : import('../lib/guideHelp.json').then(m => { DATA = m.default || m; return DATA })

export const openGuide = detail => window.dispatchEvent(new CustomEvent('hc-guide-open', { detail }))

// **label** -> bold (the guide marks buttons and menu names that way)
const Rich = ({ text }) => <>{String(text || '').split(/(\*\*[^*]+\*\*)/g).map((s, i) => /^\*\*[^*]+\*\*$/.test(s) ? <b key={i}>{s.slice(2, -2)}</b> : s)}</>
const Steps = ({ steps }) => steps && steps.length ? <ol style={{ margin: '6px 0 10px', paddingLeft: 22, lineHeight: 1.5 }}>{steps.map((s, i) => <li key={i} style={{ marginBottom: 3 }}><Rich text={s} /></li>)}</ol> : null
const Tips = ({ tips }) => (tips || []).map((t, i) => (
  <div key={i} style={{ margin: '6px 0', padding: '7px 10px', borderRadius: 8, fontSize: 13, background: t.kind === 'careful' ? 'rgba(192,67,59,.08)' : 'rgba(176,138,46,.08)', borderLeft: '3px solid ' + (t.kind === 'careful' ? '#c0433b' : 'var(--gold)') }}>
    <b style={{ textTransform: 'capitalize' }}>{t.kind}: </b><Rich text={t.text} />
  </div>
))
const lab = { fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)', marginTop: 10 }

export default function GuideHelpPanel({ aiAllowed }) {
  const [want, setWant] = useState(null)     // { id } | { page }
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    const h = e => { const d = (e && e.detail) || {}; if (!d.id && !d.page) return; setErr(''); setWant(d); loadGuide().then(setData).catch(() => setErr('The guide could not be loaded - check the connection and try again.')) }
    const cl = () => setWant(null)
    window.addEventListener('hc-guide-open', h)
    window.addEventListener('hc-guide-close', cl)
    return () => { window.removeEventListener('hc-guide-open', h); window.removeEventListener('hc-guide-close', cl) }
  }, [])
  useEffect(() => {
    if (!want) return
    const k = ev => { if (ev.key === 'Escape') setWant(null) }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [want])
  if (!want) return null
  const close = () => setWant(null)
  const entries = data && data.entries
  const e = entries ? (want.id ? entryById(entries, want.id) : pageEntry(entries, want.page)) : null
  const ask = () => { close(); window.dispatchEvent(new CustomEvent('hc-assistant-open', { detail: { text: 'How do I use ' + (e ? e.title : 'this page') + '?' } })) }
  const footer = <>
    <button className="btn" style={{ marginRight: 'auto' }} onClick={() => { close(); window.dispatchEvent(new CustomEvent('hc-tour-start')) }}>Restart tutorial</button>
    {e && e.video && <button className="btn" onClick={() => window.open(e.video, '_blank', 'noopener')}>{'▶'} Guide video</button>}
    {aiAllowed && e && <button className="btn" onClick={ask}>Ask the assistant</button>}
    <button className="btn btn-primary" onClick={close}>Close</button>
  </>
  // on document.body: outside the page area, whose phone rules (AppShell .hc-content) would re-flow it
  return createPortal(
    <div className="hc-guide-overlay" onClick={ev => { if (ev.target === ev.currentTarget) close() }}
      style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(4,10,20,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'max(12px, env(safe-area-inset-top)) 12px max(12px, env(safe-area-inset-bottom))' }}>
      <div role="dialog" aria-modal="true" aria-label={'Guide: ' + (e ? e.title : '')} className="hc-guide-panel fade-up"
        style={{ background: 'var(--ink2, var(--bg))', color: 'var(--text)', border: '0.5px solid var(--border2, var(--border))', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '100%', display: 'flex', flexDirection: 'column', flexWrap: 'nowrap', overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px 12px', borderBottom: '0.5px solid var(--border)', flexShrink: 0 }}>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{e ? e.title : 'Guide'}</div>
          <button onClick={close} aria-label="Close" style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 16, cursor: 'pointer', padding: '2px 6px' }}>{'\u2715'}</button>
        </div>
      <div className="hc-guide-entry" style={{ fontSize: 13.5, padding: '14px 18px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {err && <div className="err-box">{err}</div>}
        {!err && !data && <div style={{ color: 'var(--muted)' }}>Loading the guide…</div>}
        {data && !e && <div style={{ color: 'var(--muted)' }}>There is no guide page for this page yet. The AI assistant can explain it.</div>}
        {e && <>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{e.chapterTitle} · {e.num}{e.pdfPage ? ' · ' + data.guide.replace(/ · .*/, '') + ', page ' + e.pdfPage : ''}</div>
          {e.what && <p style={{ margin: '8px 0' }}><Rich text={e.what} /></p>}
          {e.see && <><div style={lab}>What you see</div><p style={{ margin: '4px 0' }}><Rich text={e.see} /></p></>}
          {e.why && <p style={{ margin: '4px 0' }}><b>Why. </b><Rich text={e.why} /></p>}
          {e.who && <><div style={lab}>Who can use it</div><div><Rich text={e.who} /></div></>}
          {e.where && <><div style={lab}>Where to find it</div><div><b>{e.where}</b></div></>}
          {e.steps.length > 0 && <div style={lab}>{e.see ? 'What to do' : 'Steps'}</div>}
          <Steps steps={e.steps} />
          {e.table && <ul style={{ paddingLeft: 18 }}>{e.table.map((r, i) => <li key={i}><Rich text={r} /></li>)}</ul>}
          <Tips tips={e.tips} />
          {e.sub.map(s => (
            <details key={s.id} style={{ margin: '8px 0', borderTop: '0.5px solid var(--border)', paddingTop: 6 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{s.title}</summary>
              {s.what && <p style={{ margin: '6px 0' }}><Rich text={s.what} /></p>}
              {s.who && <div style={{ fontSize: 12.5 }}><b>Who: </b><Rich text={s.who} /></div>}
              <Steps steps={s.steps} />
              <Tips tips={s.tips} />
            </details>
          ))}
          {e.try.length > 0 && <><div style={lab}>Try saying (to the AI assistant)</div>{e.try.map((t, i) => <div key={i} style={{ fontStyle: 'normal' }}>“<Rich text={t} />”</div>)}</>}
        </>}
      </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', padding: '12px 18px', borderTop: '0.5px solid var(--border)', flexShrink: 0 }}>{footer}</div>
      </div>
    </div>, document.body)
}

// the "?" in the page header: the guide entry of this page
export function PageGuideButton({ page, style }) {
  if (!PAGE_GUIDE[page]) return null
  return (
    <button type="button" className="hc-guide-btn" onClick={() => openGuide({ page })} title="Help: this page in the Hey Captain guide" aria-label="Help: this page in the guide"
      style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--line, var(--border))', background: 'transparent', color: 'var(--text)', fontWeight: 700, fontSize: 14, cursor: 'pointer', flex: '0 0 auto', marginLeft: 8, ...style }}>?</button>
  )
}
